"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getUpgradeablePackage = getUpgradeablePackage;
const buildPackageOption_1 = __importDefault(require("./lib/buildPackageOption"));
const compareUpgradeablePackage_1 = __importDefault(require("./lib/compareUpgradeablePackage"));
const groupAndSortPackages_1 = __importDefault(require("./lib/groupAndSortPackages"));
/**
 * Finds the most suitable upgradeable package option for a guest's hotel booking.
 *
 * @param leHotelOffer - The complete hotel offer containing all available packages and options
 * @param selectedPackageOption - The package that the guest has currently selected
 * @param occupants - Information about the guests who will be staying (used for capacity validation)
 * @param availableRoomsByPackage - Room availability data mapped by package for inventory checks
 *
 * @returns The upgradeable package that:
 * - Is always more expensive than the selected package
 * - Has the same cancellation policy as the selected package
 * - Meets availability and capacity requirements for the given occupants
 * - Is either from the same room type but a higher tier package, OR from a higher tier room type
 * - Is the least expensive option among all qualifying upgradeable packages
 *
 * Returns undefined if no suitable upgrade package is found.
 *
 * @throws Will throw an error if any processing issues occur during package analysis.
 */
function getUpgradeablePackage(leHotelOffer, selectedPackageOption, occupants, availableRoomsByPackage) {
    // Map server packages to PackageOptionView with proper properties
    const mappedPackageOptions = Object.values(leHotelOffer.packages).map(pkg => (0, buildPackageOption_1.default)(pkg, leHotelOffer, leHotelOffer.options, occupants, availableRoomsByPackage));
    // Map selected package to PackageOptionView
    const mappedSelectedPackage = (0, buildPackageOption_1.default)(selectedPackageOption, leHotelOffer, leHotelOffer.options, occupants, availableRoomsByPackage);
    // 1. Get packages by room
    const packagesByRoom = (0, groupAndSortPackages_1.default)(mappedPackageOptions);
    // 2. Get the packages for the selected room
    const indexForSelectedRoom = packagesByRoom.findIndex(option => option.roomId === mappedSelectedPackage.package.roomType?.id);
    const packagesForSelectedRoom = packagesByRoom[indexForSelectedRoom].packageDetails;
    // 3. Exclude the selected package and any packages of the same room type that are considered a lower tier compared with the selected package
    const indexForSelectedPackage = packagesForSelectedRoom.findIndex(option => option.package.uniqueKey === mappedSelectedPackage.package.uniqueKey);
    const higherTierPackagesForSelectedRoom = packagesForSelectedRoom.slice(indexForSelectedPackage + 1);
    // 4. Create an array combining all packages, excluding lower tier rooms, and lower tier packages of the selected room
    const packagesForHigherTierRooms = packagesByRoom
        .slice(indexForSelectedRoom + 1)
        .flatMap(room => room.packageDetails);
    // 5. From the potential higher tier packages, check the cancellation policy, availability and price to find the upgradeable packages
    const allUpgradablePackages = [
        ...higherTierPackagesForSelectedRoom,
        ...packagesForHigherTierRooms,
    ].filter(option => option.hotelAvailable &&
        option.capacityValid &&
        (0, compareUpgradeablePackage_1.default)(option, mappedSelectedPackage));
    // 6. Find the cheapest package (whether that is from the same tier of room or in the room tiers above)
    const minPrice = Math.min(...allUpgradablePackages.map(p => p.hotelPrice));
    /* 7. Return the upgradable package which is:
     * - Always more expensive than the selected package
     * - Has the same cancellation policy
     * - Availability / capacity requirements are met
     * - From the same room type but a higher tier package OR from a highter tier room type
     * - Off all the upgradeable packages, is the least expensive
     */
    return allUpgradablePackages.find(p => p.hotelPrice === minPrice);
}
